/**
 * Shared layout for V2 admin Properties / Rooms / Tenants pages.
 *
 * Renders:
 *   - sticky V2 admin nav strip (Properties / Lessors / back-to-console)
 *   - breadcrumb row
 *   - top bar with title + optional action slot
 *   - main content area (children)
 *   - optional right sidebar slot (sticky on desktop, below content on mobile)
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { V2AdminNavbar } from "./V2AdminNavbar";
import { PAGE_TITLE_CLASS } from "./page-title";

export type Crumb = {
  label: string;
  to?: string; // omit for the current page
};

type Props = {
  /** Omit to render the page's own heading inside its content column (Brain). */
  title?: string;
  /** One line under the title saying what the page is for. Same slot the
   *  legacy console's CardDescription fills on Bookings & Tenants, so a page
   *  reads the same whichever admin it lives in. */
  description?: ReactNode;
  /** Rendered directly under the title (e.g. a "Hidden" badge) — not tucked
   *  inside an action menu or a sidebar card where it's easy to miss. */
  titleBadge?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  sidebar?: ReactNode;
  /** Put the header and the content on ONE white card, the way Bookings &
   *  Tenants does in the legacy console, instead of a bare header floating
   *  over the page background. Opt-in: list pages where the header, filters
   *  and table are one object read better contained; detail pages, which are
   *  already a stack of their own cards, do not. */
  surface?: boolean;
  children: ReactNode;
};

export function AdminV2Layout({
  title,
  description,
  titleBadge,
  breadcrumbs,
  actions,
  sidebar,
  surface,
  children,
}: Props) {
  // The sidebar column is only laid out when a sidebar is actually passed. It
  // used to be in the template unconditionally, so every sidebar-less page
  // (Owners, Directory, Operators, Translations) reserved 320px + the gap for
  // nothing and rendered its table ~344px narrower than the shell it sits in.
  const bodyGrid = sidebar
    ? "grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]"
    : "grid gap-6";

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1
          className={
            surface ? "text-xl font-semibold tracking-tight" : PAGE_TITLE_CLASS
          }
        >
          {title}
        </h1>
        {description && (
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        )}
        {titleBadge && <div className="mt-1.5">{titleBadge}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );

  const body = (
    <div className={bodyGrid}>
      <main className="min-w-0">
        {surface ? (
          <Card className="border-border/60 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-border/40 bg-muted/20 pb-4">
              {header}
            </CardHeader>
            <CardContent className="pt-6">{children}</CardContent>
          </Card>
        ) : (
          children
        )}
      </main>
      {sidebar && (
        // top-20 = 12 (sticky V2 nav) + 8 (top padding) — keeps sidebar
        // visible below the nav when scrolled.
        <aside className="lg:sticky lg:top-20 lg:self-start space-y-4">
          {sidebar}
        </aside>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <V2AdminNavbar />
      {/* pt-6 = breathing room below the V2 nav strip only. The public-site
          marketing nav is hidden on V2 routes (Navigation.tsx returns null)
          so we don't pad for it. */}
      {/* The shell used to stop at max-w-screen-2xl (1536px), which left a
          ~400px gutter on the 1920px screens admins actually work on while
          list columns truncated. Widened to 1920px so the content uses the
          window, still capped so text lines stay readable on ultrawides. */}
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8 pt-6 pb-10">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            aria-label="breadcrumb"
            className="mb-4 flex items-center text-sm text-muted-foreground"
          >
            {breadcrumbs.map((c, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <span key={`${c.label}-${i}`} className="flex items-center">
                  {c.to && !isLast ? (
                    <Link
                      to={c.to}
                      className="hover:text-foreground transition-colors"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span
                      className={isLast ? "text-foreground font-medium" : ""}
                    >
                      {c.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight className="mx-2 h-4 w-4" />}
                </span>
              );
            })}
          </nav>
        )}

        {!surface && title && <header className="mb-6">{header}</header>}
        {body}
      </div>
    </div>
  );
}
